import SleepActionModal from "@/components/mirror/SleepActionModal";
import { supabase } from "@/integrations/supabase/client";
import { MIRROR_SLEEP_CARDS_ENABLED } from "@/lib/featureFlags";
import { sleepOriginOperationId, sleepOwnerSession, sleepUuid, type SleepActionPrefill } from "@/lib/sleepExecutor";
import { SLEEP_EDITABLE_FIELDS, SLEEP_PREFERENCE_FIELDS } from "../../../../supabase/functions/_shared/sleepActionDomain";
import type { CaptureContext, ModuleCaptureHandler } from "@/lib/capture/types";

const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const clock = (value: unknown): value is string => typeof value === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
const qualities = ["amazing", "good", "fair", "poor", "terrible"];
// The old extractor supplies a default quality even when the user says none.
// Only an explicitly spoken quality becomes a draft; all drafts remain reviewable.
const qualityMentioned = (text: string) => /\b(?:bem|mal|p[eé]ssim[oa]|horr[ií]vel|incr[ií]vel|mais ou menos|well|good|great|amazing|poorly|badly|terrible|awful|so-so|goed|slecht|geweldig|uitstekend|verschrikkelijk|bien|mauvais|malissimo|bene|schlecht|gut|dobrze|źle|[oó]timo|bad|perfect)\b/iu.test(text);

async function origin(ctx: CaptureContext, actionId: string) {
  if (!sleepUuid(ctx.ownerId) || !sleepUuid(ctx.sessionId)) return null;
  const owner = ctx.ownerId, session = ctx.sessionId, assert = () => ctx.assertCurrent?.();
  assert();
  const operation_id = await sleepOriginOperationId(owner, session, actionId, "operation", assert);
  assert();
  return { action_id: actionId, operation_id, expected_user_id: owner, expected_session_id: session };
}

/** Model fields are drafts; fresh owned state and write proof come from review. */
export const sleepActionCaptureHandler: ModuleCaptureHandler<SleepActionPrefill> = {
  tool: "propose_sleep_action",
  isEnabled: () => MIRROR_SLEEP_CARDS_ENABLED,
  getRawText: proposal => proposal.tool === "propose_sleep_action" ? JSON.stringify(proposal.args) : "",
  extract: async (_raw, proposal, ctx) => {
    if (!MIRROR_SLEEP_CARDS_ENABLED || proposal.tool !== "propose_sleep_action" ||
      !["create", "update", "delete", "save_preferences"].includes(proposal.args.action)) return null;
    const args = structuredClone(proposal.args);
    if (["update", "delete"].includes(args.action) && !sleepUuid(args.sleep_id)) return null;
    const pinned = await origin(ctx, proposal.action_id);
    if (!pinned) return null;
    const fields = object(args.fields) && ["create", "update"].includes(args.action)
      ? Object.fromEntries(Object.entries(args.fields).filter(([key]) => (args.action === "create" && key === "sleep_date" || SLEEP_EDITABLE_FIELDS.includes(key as typeof SLEEP_EDITABLE_FIELDS[number])))) : undefined;
    const preferences = object(args.preferences) && args.action === "save_preferences"
      ? Object.fromEntries(Object.entries(args.preferences).filter(([key]) => key !== "user_id" && SLEEP_PREFERENCE_FIELDS.includes(key as typeof SLEEP_PREFERENCE_FIELDS[number]))) : undefined;
    return { ...pinned, action: args.action,
      ...(sleepUuid(args.sleep_id) && args.action !== "create" && args.action !== "save_preferences" ? { sleep_id: args.sleep_id.toLowerCase() } : {}),
      ...(fields ? { fields } : {}), ...(preferences ? { preferences_patch: preferences } : {}),
    } as SleepActionPrefill;
  },
  renderModal: props => <SleepActionModal {...props} />,
};

/** Persisted voice proposals enter the same reviewed and journaled executor. */
export const sleepCaptureHandler: ModuleCaptureHandler<SleepActionPrefill> = {
  tool: "propose_sleep_log",
  isEnabled: () => MIRROR_SLEEP_CARDS_ENABLED,
  getRawText: proposal => proposal.tool === "propose_sleep_log" && typeof proposal.args.raw_text === "string" ? proposal.args.raw_text.trim() : "",
  extract: async (rawText, proposal, ctx) => {
    if (!MIRROR_SLEEP_CARDS_ENABLED || proposal.tool !== "propose_sleep_log" || !rawText ||
      !sleepUuid(ctx.ownerId) || !sleepUuid(ctx.sessionId)) return null;
    const owner = ctx.ownerId, session = ctx.sessionId, actionId = proposal.action_id;
    const transcript = String(rawText), language = ctx.lang, assert = () => ctx.assertCurrent?.();
    assert();
    const pinned = await sleepOwnerSession(owner, assert);
    assert();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const { data, error } = await supabase.functions.invoke("sleep-extract", {
      body: { transcript, language, user_timezone: timezone },
      headers: { Authorization: "Bearer " + pinned.access_token },
    });
    assert();
    await sleepOwnerSession(owner, assert);
    assert();
    if (error) { await ctx.surfaceQuota(error); assert(); return null; }
    if (!object(data) || typeof data.confidence !== "number" || !Number.isFinite(data.confidence) || data.confidence < .25) return null;
    const fields = { ...(clock(data.bedtime) ? { bedtime: data.bedtime } : {}), ...(clock(data.wakeup) ? { wakeup: data.wakeup } : {}),
      ...(typeof data.quality === "string" && qualities.includes(data.quality) && qualityMentioned(transcript) ? { quality: data.quality } : {}),
    };
    // Partial inference is displayed rather than silently invented as authority.
    const operation_id = await sleepOriginOperationId(owner, session, actionId, "operation", assert);
    assert();
    return { action: "create", fields, source: "voice", transcriptRaw: transcript, legacy_inferred: true,
      expected_user_id: owner, expected_session_id: session, action_id: actionId, operation_id } as SleepActionPrefill;
  },
  renderModal: props => <SleepActionModal {...props} />,
};
